import Decimal from 'decimal.js';
import { compact } from 'lodash';

import { SelectInputOption } from '@/components/form/baseInputs/inputTypes';
import {
  getPercentMaxValidation,
  getRequiredValidation,
} from '@/components/utils/inputUtils';
import { ENTITY_TYPES } from '@/modules/entities/entities.constants';
import {
  DispositiveProvisionDispositionKind,
  DispositiveProvisionTemplateKind,
  EntityKind,
} from '@/types/schema';
import { maxDecimalJS, minDecimalJS } from '@/utils/decimalJSUtils';
import { UnreachableError } from '@/utils/errors';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { otherDispositionKindOptions } from '../DispositiveProvisionsForm.constants';
import { DispositiveProvisionsFormRecipient } from '../DispositiveProvisionsForm.types';
import { useRemainingExemptionAmountForClient } from '../hooks/useRemainingExemptionAmountForClient';
import { DispositiveProvisionsTemplateRules } from './DispositiveProvisionsRecipient.constants';
import {
  DispositiveProvisionsRecipientTemplateRecipientConfiguration,
  DispositiveProvisionsRecipientTemplateRules,
  DispositiveProvisionsRecipientTypes,
} from './DispositiveProvisionsRecipient.types';

function getCurrencyDisplayValue(value?: Decimal) {
  if (value === undefined) {
    return '';
  }

  return ` (${formatCurrencyNoDecimals(value)})`;
}

export function getDispositionKindOption(
  option: (typeof otherDispositionKindOptions)[0],
  remainingExemptionAmountForGrantor: ReturnType<
    typeof useRemainingExemptionAmountForClient
  >['data'],
  filterDispositionKindSelectOptions: DispositiveProvisionDispositionKind[],
  isTemplateMode: boolean = false
) {
  const { display, value } = option;

  // note: if in template mode, we don't want to display any dollar values, as they can be
  // incorrect because of death order
  if (typeof display === 'function') {
    switch (value) {
      case DispositiveProvisionDispositionKind.RemainingLifetimeExclusionOfGrantor: {
        if (isTemplateMode) {
          return {
            display: display(''),
            value,
            disabled: filterDispositionKindSelectOptions.includes(value),
          };
        }
        return {
          display: display(
            getCurrencyDisplayValue(
              remainingExemptionAmountForGrantor?.remainingLifetimeExclusion
            )
          ),
          value,
          disabled: filterDispositionKindSelectOptions.includes(value),
        };
      }
      case DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantorInExcessOfLifetimeExclusion: {
        if (isTemplateMode) {
          return {
            display: display(''),
            value,
            disabled: filterDispositionKindSelectOptions.includes(value),
          };
        }
        return {
          display: display(
            getCurrencyDisplayValue(
              remainingExemptionAmountForGrantor?.remainingGstExclusionInExcessOfLifetime
            )
          ),
          value,
          disabled: filterDispositionKindSelectOptions.includes(value),
        };
      }
      case DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantor: {
        if (isTemplateMode) {
          return {
            display: display(''),
            value,
            disabled: filterDispositionKindSelectOptions.includes(value),
          };
        }
        return {
          display: display(
            getCurrencyDisplayValue(
              remainingExemptionAmountForGrantor?.remainingGstExclusion
            )
          ),
          value,
          disabled: filterDispositionKindSelectOptions.includes(value),
        };
      }
      case DispositiveProvisionDispositionKind.RemainingStateExemption: {
        const stateCode = remainingExemptionAmountForGrantor?.stateCode;
        const displayStateCode = stateCode ?? 'state';

        if (isTemplateMode) {
          return {
            display: display(displayStateCode, ''),
            value,
            disabled: filterDispositionKindSelectOptions.includes(value),
          };
        }

        return {
          display: display(
            displayStateCode,
            getCurrencyDisplayValue(
              stateCode
                ? remainingExemptionAmountForGrantor?.remainingStateExemption
                : undefined
            )
          ),
          value,
          disabled: filterDispositionKindSelectOptions.includes(value),
        };
      }
      case DispositiveProvisionDispositionKind.RemainingFederalLifetimeExemptionInExcessOfStateExemption: {
        const stateCode = remainingExemptionAmountForGrantor?.stateCode;
        const displayStateCode = stateCode ?? 'state';

        if (isTemplateMode) {
          return {
            display: display(displayStateCode, ''),
            value,
            disabled: filterDispositionKindSelectOptions.includes(value),
          };
        }

        return {
          display: display(
            displayStateCode,
            getCurrencyDisplayValue(
              stateCode
                ? remainingExemptionAmountForGrantor?.remainingFederalLifetimeExemptionInExcessOfStateExemption
                : undefined
            )
          ),
          value,
          disabled: filterDispositionKindSelectOptions.includes(value),
        };
      }
      case DispositiveProvisionDispositionKind.LesserOfRemainingStateOrFederalExemption: {
        if (isTemplateMode) {
          return {
            display: display(''),
            value,
            disabled: filterDispositionKindSelectOptions.includes(value),
          };
        }
        return {
          display: display(
            getCurrencyDisplayValue(
              minDecimalJS(
                compact([
                  remainingExemptionAmountForGrantor?.remainingStateExemption,
                  remainingExemptionAmountForGrantor?.remainingLifetimeExclusion,
                ]),
                new Decimal(0)
              )
            )
          ),
          value,
          disabled: filterDispositionKindSelectOptions.includes(value),
        };
      }
      default:
        throw new UnreachableError({
          case: value as never,
          message: 'Unhandled disposition option kind',
        });
    }
  } else {
    return {
      display,
      value,
      disabled: filterDispositionKindSelectOptions.includes(value),
    };
  }
}

// All formulaic disposition kinds whose percentages should be tracked
// and restricted once they sum to 100% across recipients
const FORMULAIC_DISPOSITION_KINDS: DispositiveProvisionDispositionKind[] = [
  DispositiveProvisionDispositionKind.RemainingLifetimeExclusionOfGrantor,
  DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantorInExcessOfLifetimeExclusion,
  DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantor,
  DispositiveProvisionDispositionKind.RemainingStateExemption,
  DispositiveProvisionDispositionKind.RemainingFederalLifetimeExemptionInExcessOfStateExemption,
  DispositiveProvisionDispositionKind.LesserOfRemainingStateOrFederalExemption,
  DispositiveProvisionDispositionKind.AnythingLeftOver,
];

/**
 * Determines which formulaic disposition kinds should be disabled
 * based on the total percentage of formula allocated across all recipients.
 * A kind is disabled once its percentages sum to >= 100%.
 * Recipients with no explicit percentage (null/undefined) default to 100%.
 *
 * ALO (Anything Left Over) is order-dependent: only consecutive ALO
 * recipients form a group. A non-ALO recipient in between resets the ALO
 * running total, matching the behavior of calculateResiduaryRecipients.
 */
export function getDispositionKindsToDisable(
  recipients: Pick<
    DispositiveProvisionsFormRecipient,
    'dispositionKind_Select' | 'dispositionFormulaicPercentage'
  >[]
): DispositiveProvisionDispositionKind[] {
  // Non-ALO formulaic kinds reference specific exemption pools, so a global
  // sum across all recipients is correct regardless of order.
  const percentageSumByKind = new Map<
    DispositiveProvisionDispositionKind,
    Decimal
  >();

  // ALO is order-dependent: only consecutive ALO recipients form a group.
  let aloConsecutiveSum = new Decimal(0);
  let aloMaxGroupSum = new Decimal(0);

  for (const r of recipients) {
    if (
      r.dispositionKind_Select === '' ||
      !FORMULAIC_DISPOSITION_KINDS.includes(r.dispositionKind_Select)
    ) {
      continue;
    }

    const percentage = r.dispositionFormulaicPercentage ?? new Decimal(100);

    if (
      r.dispositionKind_Select ===
      DispositiveProvisionDispositionKind.AnythingLeftOver
    ) {
      aloConsecutiveSum = aloConsecutiveSum.plus(percentage);
      aloMaxGroupSum = maxDecimalJS(
        [aloMaxGroupSum, aloConsecutiveSum],
        aloMaxGroupSum
      );
    } else {
      // Non-ALO formulaic recipient interrupts the ALO consecutive group
      aloConsecutiveSum = new Decimal(0);

      const currentSum =
        percentageSumByKind.get(r.dispositionKind_Select) ?? new Decimal(0);
      percentageSumByKind.set(
        r.dispositionKind_Select,
        currentSum.plus(percentage)
      );
    }
  }

  const itemsToDisable: DispositiveProvisionDispositionKind[] = [];

  if (aloMaxGroupSum.gte(100)) {
    itemsToDisable.push(DispositiveProvisionDispositionKind.AnythingLeftOver);
  }

  percentageSumByKind.forEach((sum, kind) => {
    if (sum.gte(100)) {
      itemsToDisable.push(kind);
    }
  });

  return itemsToDisable;
}

export function validateRecipient(
  recipientId: string,
  recipientOptions: SelectInputOption[]
): string | undefined {
  const pulidKind = getPulidKind(recipientId);
  switch (pulidKind) {
    case PulidKind.ClientProfile: {
      const recipientOption = recipientOptions.find((option) => {
        return 'value' in option && option.value === recipientId;
      });

      // this is currently the only way for a client profile recipient to be invalid
      if (recipientOption?.disabled) {
        return 'Recipient must be a living client';
      }

      return undefined;
    }

    case PulidKind.TestamentaryEntity: {
      const recipientOption = recipientOptions.find((option) => {
        return 'value' in option && option.value === recipientId;
      });

      if (recipientOption?.disabled) {
        return 'Testamentary entity must be valid for this death scenario';
      }

      return undefined;
    }

    default:
      return undefined;
  }
}

interface GetRecipientValidationErrorParams {
  recipient: DispositiveProvisionsFormRecipient | undefined;
  recipientOptions: SelectInputOption[];
  recipientIsRequired: boolean;
}

const validateRequiredRecipientType = getRequiredValidation(
  'Recipient type',
  true
);
const validateRequiredDistributionFormula = getRequiredValidation(
  'Distribution formula',
  true
);
const validateDistributionPercentage = getPercentMaxValidation({
  isDecimalJSInput: true,
  label: 'Distribution percentage',
  maxValue: 100,
});
const validateFormulaPercentage = getPercentMaxValidation({
  isDecimalJSInput: true,
  label: 'Percentage of formula',
  maxValue: 100,
});
const validateAssetPercentage = getPercentMaxValidation({
  isDecimalJSInput: true,
  label: 'Percentage of asset',
  maxValue: 100,
});

/**
 * Validates recipient fields that can be unmounted while their card is
 * collapsed. The required and percentage rules use the same validators as
 * the form-aware inputs. This function only selects the active fields.
 */
export function getRecipientValidationError({
  recipient,
  recipientOptions,
  recipientIsRequired,
}: GetRecipientValidationErrorParams): string | undefined {
  if (!recipient) return undefined;

  const recipientRequiredError = getRequiredValidation(
    'Recipient',
    recipientIsRequired
  )(recipient.recipientId);
  if (recipientRequiredError) return recipientRequiredError;

  if (recipient.recipientId) {
    const recipientError = validateRecipient(
      recipient.recipientId,
      recipientOptions
    );
    if (recipientError) return recipientError;

    const recipientTypeError = validateRequiredRecipientType(
      recipient.transferTaxKind
    );
    if (recipientTypeError) return recipientTypeError;

    if (recipient.dispositionKind_ButtonGroup === 'other') {
      const distributionFormulaError = validateRequiredDistributionFormula(
        recipient.dispositionKind_Select
      );
      if (distributionFormulaError) return distributionFormulaError;
    }
  }

  if (
    recipient.dispositionKind_ButtonGroup ===
    DispositiveProvisionDispositionKind.Percentage
  ) {
    return validateDistributionPercentage(recipient.dispositionPercentage);
  }
  if (recipient.dispositionKind_ButtonGroup === 'other') {
    return validateFormulaPercentage(recipient.dispositionFormulaicPercentage);
  }
  if (recipient.dispositionKind_ButtonGroup === 'asset') {
    return validateAssetPercentage(recipient.dispositionAssetPercentage);
  }

  return undefined;
}

export function getTemplateRulesForTemplateType(
  templateType: DispositiveProvisionTemplateKind | null | undefined,
  externalRecipientConfiguration?: Partial<DispositiveProvisionsRecipientTemplateRecipientConfiguration>
): {
  templateRules: DispositiveProvisionsRecipientTemplateRules;
  recipientConfiguration: DispositiveProvisionsRecipientTemplateRecipientConfiguration;
} {
  const isPouroverWillTemplate =
    templateType === DispositiveProvisionTemplateKind.PouroverWill;

  if (isPouroverWillTemplate) {
    const recipientConfiguration: DispositiveProvisionsRecipientTemplateRecipientConfiguration =
      {
        ...DispositiveProvisionsTemplateRules[templateType]
          .recipientConfiguration,
        allowsTestamentaryEntityRecipient: true,
        customEntityFilter: (entity) =>
          [EntityKind.RevocableTrust, EntityKind.IrrevocableTrust].includes(
            entity.kind
          ),
        createRecipientTypes: [
          DispositiveProvisionsRecipientTypes.Entity,
          DispositiveProvisionsRecipientTypes.TestamentaryEntity,
        ],
        limitEntityTypes: [
          ENTITY_TYPES.REVOCABLE_TRUST,
          ENTITY_TYPES.IRREVOCABLE_TRUST,
        ],
        createEntityType: undefined,
        forceEntityType: false,
      };
    return {
      templateRules: {
        ...DispositiveProvisionsTemplateRules[templateType],
        recipientConfiguration,
      },
      recipientConfiguration,
    };
  } else if (
    templateType &&
    // treat default template as a non-template for the purposes of recipient configuration
    templateType !== DispositiveProvisionTemplateKind.Default
  ) {
    return {
      templateRules: DispositiveProvisionsTemplateRules[templateType],
      recipientConfiguration: {
        ...(DispositiveProvisionsTemplateRules[templateType]
          .recipientConfiguration || {}),
        ...(externalRecipientConfiguration || {}),
      },
    };
  }
  return {
    templateRules: DispositiveProvisionsTemplateRules.NO_TEMPLATE,
    recipientConfiguration: {
      ...(DispositiveProvisionsTemplateRules.NO_TEMPLATE
        .recipientConfiguration || {}),
      ...(externalRecipientConfiguration || {}),
    },
  };
}
